import * as React from "react";

import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import {
  PasswordInput,
  PasswordRequirements,
  type PasswordRequirement
} from "@libs/zapi-ui/ds/components/password-input";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";

type ChangePasswordValues = { currentPassword: string; newPassword: string; confirmPassword: string };

type ChangePasswordResult = { ok: true } | { ok: false; error?: string };

type ChangePasswordLabels = {
  title: string;
  description: string;
  current: string;
  new: string;
  confirm: string;
  show: string;
  hide: string;
  met: string;
  notMet: string;
  submit: string;
  cancel: string;
  close: string;
  loading: string;
};

const defaultLabels: ChangePasswordLabels = {
  title: "Change password",
  description: "Enter your current password, then the new one twice.",
  current: "Current password",
  new: "New password",
  confirm: "Confirm new password",
  show: "Show password",
  hide: "Hide password",
  met: "met",
  notMet: "not met",
  submit: "Change password",
  cancel: "Cancel",
  close: "Close",
  loading: "Loading"
};

const empty: ChangePasswordValues = { currentPassword: "", newPassword: "", confirmPassword: "" };

function ChangePasswordDialog({
  open,
  onOpenChange,
  requirements,
  canSubmit,
  labels: labelOverrides,
  onSubmit
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rules for the new password, as met so far. */
  requirements: (values: ChangePasswordValues) => PasswordRequirement[];
  canSubmit: (values: ChangePasswordValues) => boolean;
  labels?: Partial<ChangePasswordLabels>;
  /** Changes the password. An error keeps the dialog open with it under the current password. */
  onSubmit: (values: ChangePasswordValues) => Promise<ChangePasswordResult>;
}) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const [values, setValues] = React.useState(empty);
  const [error, setError] = React.useState<string>();
  const [submitting, setSubmitting] = React.useState(false);
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValues(empty);
      setError(undefined);
    }
  }
  const ids = { currentPassword: React.useId(), newPassword: React.useId(), confirmPassword: React.useId() };

  const submit = async () => {
    if (!canSubmit(values)) return;
    setSubmitting(true);
    setError(undefined);
    const result = await onSubmit(values);
    setSubmitting(false);
    if (result.ok) onOpenChange(false);
    else setError(result.error);
  };

  const field = (key: keyof ChangePasswordValues, label: string, autoComplete: string, autoFocus = false) => (
    <Field data-invalid={key === "currentPassword" && error ? true : undefined}>
      <FieldLabel htmlFor={ids[key]}>{label}</FieldLabel>
      <PasswordInput
        id={ids[key]}
        autoComplete={autoComplete}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        value={values[key]}
        disabled={submitting}
        showLabel={labels.show}
        hideLabel={labels.hide}
        aria-invalid={key === "currentPassword" && error ? true : undefined}
        onChange={(event) => {
          const value = event.target.value.trim();
          setValues((current) => ({ ...current, [key]: value }));
          if (key === "currentPassword") setError(undefined);
        }}
      />
      {key === "currentPassword" && <FieldError>{error}</FieldError>}
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent closeLabel={labels.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{labels.title}</DialogTitle>
          <DialogDescription className="sr-only">{labels.description}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <FieldGroup className="gap-4">
            {field("currentPassword", labels.current, "current-password", true)}
            {field("newPassword", labels.new, "new-password")}
            {field("confirmPassword", labels.confirm, "new-password")}
          </FieldGroup>
          <PasswordRequirements
            aria-live="polite"
            requirements={requirements(values)}
            metLabel={labels.met}
            unmetLabel={labels.notMet}
          />
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              shape="pill"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              {labels.cancel}
            </Button>
            <Button type="submit" shape="pill" disabled={submitting || !canSubmit(values)}>
              {submitting && <Spinner data-icon="inline-start" aria-label={labels.loading} />}
              {labels.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { ChangePasswordDialog, type ChangePasswordLabels, type ChangePasswordResult, type ChangePasswordValues };
